import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from './Theming';
import { Grid } from './Grid';
import { Hyperlink } from './Hyperlink';
import { FacebookIcon, TwitterIcon, LinkedinHeavy } from './Icon';
import { Typography } from './Typography';
import { LIGHT_MODE } from './Theme';
import { styled } from './styled';

const CenterColumn = styled(Grid)(() => ({
  textAlign: 'center',
}));

const LinkBlock = styled(Grid)(() => ({
  justifyContent: 'space-between',
}));

const Links = styled(Grid)(({ theme }) => ({
  whiteSpace: 'normal',
  [theme.breakpoints.down('lg')]: {
    paddingBottom: theme.spacing(2),
  },
}));

const LinkColumnName = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '24px',
  paddingBottom: theme.spacing(2),
}));

const BottomPaddedGrid = styled(Grid, { shouldForwardProp: (prop) => prop !== 'bottomPadding' })(({ theme, bottomPadding }) => ({
  paddingBottom: theme.spacing(bottomPadding || 1),
}));

const FooterTopRow = styled(Grid)(({ theme }) => ({
  paddingBottom: theme.spacing(1),
  [theme.breakpoints.down('lg')]: {
    paddingBottom: theme.spacing(0),
  },
}));

const FooterBottomRow = styled(Grid)(({ theme }) => ({
  paddingBottom: theme.spacing(2),
  paddingTop: theme.spacing(1),
  [theme.breakpoints.down('lg')]: {
    paddingBottom: theme.spacing(0),
    paddingTop: theme.spacing(0),
  },
}));

const styledIcon = (Component) => {
  const StyledIcon = styled(Component)(() => ({
    minWidth: '24px',
    maxWidth: '24px',
    minHeight: '24px',
    maxHeight: '24px',
    width: '24px',
    height: '24px',
  }));
  return <StyledIcon />;
};

function FooterCopyright(props) {
  const { companyName } = props;
  return (
    <CenterColumn item xs={12}>
      <Typography variant='body2' color='textSecondary'>(C) {(new Date()).getFullYear()} {companyName}. All Rights Reserved</Typography>
    </CenterColumn>
  );
}

function FooterLinks(props) {
  const { linkColor, columns } = props;
  return (
    <Grid item xs={12} md={6}>
      <LinkBlock container direction='row'>
        {columns && columns.map((col, cx) => (
          <Grid item key={['col', cx].join('-')} xs={12} sm={Math.floor(12 / Math.max(1, columns.length))} md='auto'>
            <Links container direction='column' justifyContent='center'>
              <Grid item xs={12}><LinkColumnName variant='body2' color='textPrimary'>{col.columnName}</LinkColumnName></Grid>
              {col.links.map((luPair, luPX) => (
                <BottomPaddedGrid bottomPadding={1} item key={['col', cx, luPX].join('-')} xs={12}>
                  <Typography variant='body2' color='textPrimary'>
                    <Hyperlink
                      href={luPair.url}
                      title={luPair.label}
                      aria-label={luPair.label}
                      color={linkColor}
                    >
                      {luPair.label}
                    </Hyperlink>
                  </Typography>
                </BottomPaddedGrid>
              ))}
            </Links>
          </Grid>
        ))}
      </LinkBlock>
    </Grid>
  );
}

function FooterCompanyInfo(props) {
  const {
    logo,
    logoDark,
    themeMode,
    linkColor,
  } = props;
  const ThemedLogo = (themeMode === LIGHT_MODE ? logo : logoDark);
  const logoWidth = 169;
  const logoHeight = 22;
  const themedImgArgs = { width: logoWidth, height: logoHeight, alt: 'Make My Move' };

  return (
    <Grid item xs={12} md={6}>
      <Grid container direction='column' justifyContent='center'>
        <BottomPaddedGrid item xs={12} bottomPadding={2}>
          <ThemedLogo {...themedImgArgs} />
        </BottomPaddedGrid>
        <BottomPaddedGrid item xs={12} bottomPadding={3}>
          <Typography variant='body2' color='textPrimary'>1003 E 38th St. Indianapolis, IN 46205</Typography>
        </BottomPaddedGrid>
        <Grid item xs={12}>
          <Typography color={linkColor}>
            <Hyperlink href='https://twitter.com/makemymoveusa' title='Twitter' aria-label='Twitter' color={linkColor}>{styledIcon(TwitterIcon)}</Hyperlink>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <Hyperlink href='https://www.facebook.com/makemymove/' title='Facebook' aria-label='Facebook' color={linkColor}>{styledIcon(FacebookIcon)}</Hyperlink>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <Hyperlink href='https://www.linkedin.com/company/makemymoveusa' title='LinkedIn' aria-label='LinkedIn' color={linkColor}>{styledIcon(LinkedinHeavy)}</Hyperlink>
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
}

function GlobalFooter(props) {
  const {
    className,
    logo,
    logoDark,
    columns,
  } = props;
  const theme = useTheme();

  const themeMode = theme.palette.mode;
  const linkColor = 'textPrimary';

  return (
    <footer className={className}>
      <FooterTopRow container spacing={2} direction='row' justifyContent='center' alignItems='center'>
        <FooterCompanyInfo
          themeMode={themeMode}
          linkColor={linkColor}
          logo={logo}
          logoDark={logoDark}
        />
        <FooterLinks columns={columns} linkColor={linkColor} />
      </FooterTopRow>
      <FooterBottomRow container spacing={2} direction='row' justifyContent='center' alignItems='center'>
        <FooterCopyright companyName='TMap, LLC' />
      </FooterBottomRow>
    </footer>
  );
}

GlobalFooter.defaultProps = {
  className: '',
};

GlobalFooter.propTypes = {
  className: PropTypes.string,
  logo: PropTypes.func.isRequired,
  logoDark: PropTypes.func.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      columnName: PropTypes.string.isRequired,
      links: PropTypes.arrayOf(
        PropTypes.shape({ label: PropTypes.string.isRequired, url: PropTypes.string.isRequired }),
      ).isRequired,
    }),
  ).isRequired,
};

export default GlobalFooter;
